export const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"] as const;
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];
export const CLICK_ID_TTL_DAYS = 90;

type ClickIds = Record<ClickIdKey, string | null>;

const expiresKey = (key: ClickIdKey) => `${key}_expires`;

const getLocalStorage = () => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
    if (typeof globalThis !== "undefined" && (globalThis as any).localStorage) {
      return (globalThis as any).localStorage;
    }
  } catch {
    return null;
  }
  return null;
};

const safeGetItem = (key: string): string | null => {
  try {
    const storage = getLocalStorage();
    return typeof storage?.getItem === "function" ? storage.getItem(key) : null;
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string) => {
  try {
    const storage = getLocalStorage();
    if (typeof storage?.setItem === "function") {
      storage.setItem(key, value);
    }
  } catch {
    return;
  }
};

const safeRemoveItem = (key: string) => {
  try {
    const storage = getLocalStorage();
    if (typeof storage?.removeItem === "function") {
      storage.removeItem(key);
    }
  } catch {
    return;
  }
};

const getCookieDomain = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const { hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return undefined;

  // app.dropshiptool.io / www.dropshiptool.io → .dropshiptool.io
  const parts = hostname.split(".");
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join(".")}`;
  }
  return undefined;
};

const setCookie = (name: string, value: string, days: number) => {
  if (typeof document === "undefined") return;
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const domain = getCookieDomain();
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
    if (domain) cookie += `; domain=${domain}`;
    if (window.location.protocol === "https:") cookie += "; Secure";
    document.cookie = cookie;
  } catch {
    return;
  }
};

const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${encodeURIComponent(name)}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

const persistClickId = (
  key: ClickIdKey,
  value: string,
  { overwrite = false } = {}
) => {
  // First-touch: keep the original click ID for the TTL window unless overwrite requested
  if (!overwrite) {
    const existing = getPersistedClickId(key);
    if (existing) return;
  }

  const expiresAt = Date.now() + CLICK_ID_TTL_DAYS * 864e5;
  safeSetItem(key, value);
  safeSetItem(expiresKey(key), String(expiresAt));
  setCookie(key, value, CLICK_ID_TTL_DAYS);
};

export const getPersistedClickId = (key: ClickIdKey): string | null => {
  const expiresRaw = safeGetItem(expiresKey(key));
  const expiresAt = expiresRaw !== null ? Number(expiresRaw) : NaN;
  const hasValidExpiry = Number.isFinite(expiresAt);

  if (hasValidExpiry) {
    if (Date.now() > expiresAt) {
      safeRemoveItem(key);
      safeRemoveItem(expiresKey(key));
    } else {
      const fromLs = safeGetItem(key);
      if (fromLs) return fromLs;
    }
  } else {
    // Missing or corrupted expiry (legacy/invalid data): reset the TTL from the
    // existing value so the click ID can still expire, otherwise it would persist
    // indefinitely.
    const fromLs = safeGetItem(key);
    if (fromLs) {
      const newExpiresAt = Date.now() + CLICK_ID_TTL_DAYS * 864e5;
      safeSetItem(expiresKey(key), String(newExpiresAt));
      setCookie(key, fromLs, CLICK_ID_TTL_DAYS);
      return fromLs;
    }
    // No value to keep — drop the stale/invalid expiry key if present.
    if (expiresRaw !== null) safeRemoveItem(expiresKey(key));
  }

  return getCookie(key);
};

export const getAllPersistedClickIds = (): ClickIds => ({
  gclid: getPersistedClickId("gclid"),
  gbraid: getPersistedClickId("gbraid"),
  wbraid: getPersistedClickId("wbraid"),
});

export const captureClickIdsFromSearch = (
  search: string | URLSearchParams = typeof window !== "undefined"
    ? window.location.search
    : ""
): Partial<ClickIds> => {
  const params =
    typeof search === "string"
      ? new URLSearchParams(search.startsWith("?") ? search : `?${search}`)
      : search;

  const captured: Partial<ClickIds> = {};
  for (const key of CLICK_ID_KEYS) {
    const value = params.get(key);
    if (value) {
      persistClickId(key, value);
      captured[key] = getPersistedClickId(key) || value;
    }
  }
  return captured;
};

/** Append persisted click IDs to a URL when missing (marketing → app domain hop). */
export const appendClickIdsToUrl = (url: string): string => {
  try {
    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://app.dropshiptool.io";
    const parsed = new URL(url, origin);
    const clickIds = getAllPersistedClickIds();
    for (const key of CLICK_ID_KEYS) {
      const value = clickIds[key];
      if (value && !parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, value);
      }
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * Bing-parity aliases → Dropshiptool stored keys.
 * Google ValueTrack final URLs may use either naming style.
 */
export const UTM_ALIAS_MAP: Record<string, string> = {
  utm_campaign_name: "utm_campaign",
  utm_campaign_id: "utm_campaignid",
  utm_adgroup_name: "utm_adgroup",
  utm_adgroup_id: "utm_adgroup",
  campaignid: "utm_campaignid",
  adgroupid: "utm_adgroup",
  matchtype: "utm_matchtype",
  keyword: "utm_keyword",
};

export const resolveUtmParam = (
  searchParams: URLSearchParams,
  primaryKey: string,
  ...aliasKeys: string[]
): string | null => {
  const primary = searchParams.get(primaryKey);
  if (primary) return primary;
  for (const alias of aliasKeys) {
    const value = searchParams.get(alias);
    if (value) return value;
  }
  return null;
};

export const hasClickIdAttribution = (ids: Partial<ClickIds>): boolean =>
  !!(ids.gclid || ids.gbraid || ids.wbraid);
